import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, AlertTriangle } from 'lucide-react';
import { Modal } from './ui/Modal';
import CompactSelectPicker from './ui/CompactSelectPicker';
import { exportCollection, EXPORT_FORMATS } from '../utils/collectionExport';
import { saveExportFile, saveExportBundle } from '../utils/exportSave';
import { collectionService } from '../services/collectionService';
import { hydrateCollectionsFromApi } from '../utils/collectionFolders';
import { recordFeature, ACTION } from '../services/telemetry';

export default function ExportCollectionModal({ isOpen, onClose, collection, showToast, loadFullFromApi = false }) {
  const { t } = useTranslation();
  const [format, setFormat] = useState(EXPORT_FORMATS.apiboo);
  const [lastWarnings, setLastWarnings] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [exportCollectionData, setExportCollectionData] = useState(collection);
  const [loadingFull, setLoadingFull] = useState(false);

  useEffect(() => {
    setExportCollectionData(collection);
  }, [collection]);

  useEffect(() => {
    if (!isOpen || !loadFullFromApi || !collection?.id) return;

    let cancelled = false;
    setLoadingFull(true);

    (async () => {
      try {
        const full = await collectionService.getCollection(collection.id);
        if (!cancelled) {
          const [hydrated] = hydrateCollectionsFromApi([full]);
          setExportCollectionData(hydrated || full);
        }
      } catch (err) {
        console.warn('Export: could not load full collection, using sidebar data', err);
        if (!cancelled) setExportCollectionData(collection);
      } finally {
        if (!cancelled) setLoadingFull(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, loadFullFromApi, collection]);

  const formatOptions = useMemo(
    () => [
      // First and default: the one format that loses nothing (scripts, mocks,
      // cookies, folder auth all survive), so "export then re-import" round
      // trips clean. The rest are interop with other tools.
      { value: EXPORT_FORMATS.apiboo, label: t('export.formatApiboo', { defaultValue: 'Apiboo Collection (v1.0)' }) },
      { value: EXPORT_FORMATS.postman, label: t('export.formatPostman') },
      { value: EXPORT_FORMATS.insomnia, label: t('export.formatInsomnia', { defaultValue: 'Insomnia v4' }) },
      { value: EXPORT_FORMATS.bruno, label: t('export.formatBruno', { defaultValue: 'Bruno (folder)' }) },
      { value: EXPORT_FORMATS.openapi_yaml, label: t('export.formatOpenApiYaml') },
      { value: EXPORT_FORMATS.openapi_json, label: t('export.formatOpenApiJson') },
    ],
    [t]
  );

  const preview = useMemo(() => {
    if (!exportCollectionData) return null;
    try {
      return exportCollection(exportCollectionData, { format });
    } catch (e) {
      return { error: e.message };
    }
  }, [exportCollectionData, format]);

  const handleExport = async () => {
    if (!exportCollectionData || preview?.error || exporting || loadingFull) return;

    setExporting(true);
    try {
      const result = preview.multiFile
        ? await saveExportBundle({ files: preview.files, dirName: preview.filename })
        : await saveExportFile({
            content: preview.content,
            filename: preview.filename,
            mime: preview.mime,
            format,
          });

      if (result.cancelled) {
        return;
      }

      setLastWarnings(preview.warnings || []);
      recordFeature(ACTION.EXPORT);   // identifier-free: saved, not cancelled
      showToast?.(t('export.saved'), 'success');
      onClose?.();
    } catch (err) {
      console.error('Export save failed:', err);
      showToast?.(t('export.saveFailed', { message: err.message }), 'error');
    } finally {
      setExporting(false);
    }
  };

  if (!exportCollectionData) return null;

  const warningList = preview?.warnings?.length ? preview.warnings : lastWarnings;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('export.title')} subtitle="Export your collection in a standard format" size="md">
      <p className="text-sm text-gray-600 dark:text-zinc-400 mb-4">{t('export.description')}</p>

      <div className="mb-4">
        <label className="block text-xs font-medium text-gray-600 mb-1 dark:text-zinc-400">
          {t('export.formatLabel')}
        </label>
        <div className="request-url-field-group w-full rounded-lg border border-gray-200 overflow-hidden bg-white dark:border-zinc-700 dark:bg-zinc-900">
          <div className="auth-picker-field-wrap">
            <CompactSelectPicker
              value={format}
              onChange={setFormat}
              options={formatOptions}
              className="w-full"
              aria-label={t('export.formatLabel')}
              disabled={exporting}
            />
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-500 dark:text-zinc-500 mb-4">
        {loadingFull
          ? t('export.loading', { defaultValue: 'Loading full collection…' })
          : t('export.requestCount', { count: exportCollectionData.requests?.length || 0 })}
      </p>

      {warningList.length > 0 && (
        <div className="mb-4 rounded-lg border border-gray-200 bg-amber-50 p-3 text-amber-800 dark:border-zinc-700 dark:bg-amber-950/40 dark:text-amber-200/90">
          <div className="flex items-center text-xs font-medium mb-1">
            <AlertTriangle size={14} className="flex-shrink-0 mr-2" aria-hidden />
            <span>{t('export.warnings')}</span>
          </div>
          <p className="text-xs leading-relaxed">
            {[...new Set(warningList)].join(' ')}
          </p>
        </div>
      )}

      {preview?.error && (
        <p className="text-sm text-red-600 dark:text-red-400 mb-4">{preview.error}</p>
      )}

      <button
        type="button"
        disabled={Boolean(preview?.error) || exporting}
        onClick={handleExport}
        className="flex w-full items-center justify-center space-x-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Download size={16} />
        <span>{exporting ? t('export.exporting') : t('export.button')}</span>
      </button>
    </Modal>
  );
}
